import { useEffect, useState } from 'react'
import { prettyLog } from '../../../utils'

const GEO_MANIFEST_FETCH_TIMEOUT_MS = 5000
const GEO_MANIFEST_URL =
  'https://d198ztbnlup2iq.cloudfront.net/out/v1/850e31c0e6fa4d94a44d6ccd63e4b400/index.m3u8'

// Geo probe against an Israel-only CloudFront manifest.
//
// CloudFront's default geo-restriction error page is generated at the edge
// without an Access-Control-Allow-Origin header, so a plain cors fetch()
// never sees its 403 - it just rejects with an opaque TypeError, identical
// to a genuine network failure/timeout. To tell "confirmed blocked" apart
// from "no answer", we race a second no-cors request: no-cors doesn't
// require CORS headers, so it resolves as soon as CloudFront answers at
// all, whatever the status. That lets us distinguish:
//   cors resolves 200        -> Israel
//   cors resolves non-200    -> Not Israel (origin did send CORS headers)
//   cors rejects, no-cors ok -> Not Israel (edge answered, just no CORS
//                                header on its geo-block page)
//   both reject/time out     -> Not Israel (fail open to the global stream)
export type GeoResult = 'Israel' | 'Not Israel'

export interface GeoStatus {
  country: GeoResult
  // false until the probe resolves. `country` defaults to 'Israel', so callers
  // that must NOT act on that default before geo is actually known (e.g. a live
  // asset choosing its Israel-vs-global entry) should wait for `settled`.
  settled: boolean
}

/**
 * The probe itself. 'Unknown' when neither request got an answer (no
 * network, a timeout): the live player falls open to the global stream on
 * it, a caller that blocks content must not.
 */
export const probeGeoManifest = (
  signal: AbortSignal
): Promise<GeoResult | 'Unknown'> => {
  const corsProbe = fetch(GEO_MANIFEST_URL, { signal })
  const reachabilityProbe = fetch(GEO_MANIFEST_URL, { signal, mode: 'no-cors' })
  // Also awaited below, but if corsProbe resolves first this settles later
  // (e.g. on unmount abort) with nothing else attached - silence that so it
  // doesn't surface as an unhandled rejection.
  reachabilityProbe.catch(() => undefined)

  return corsProbe
    .then(res => {
      const result: GeoResult = res.status === 200 ? 'Israel' : 'Not Israel'
      console.log('[GEO] manifest status', res.status, '->', result)
      return result
    })
    .catch(async err => {
      try {
        await reachabilityProbe
        console.log('[GEO] cors blocked but edge reachable -> Not Israel')
        return 'Not Israel'
      } catch (reachabilityErr) {
        console.log(
          '[GEO] manifest unreachable:',
          err?.name,
          (reachabilityErr as Error)?.name,
          '-> Unknown'
        )
        prettyLog('useGeoManifestFetch: 11', err, 'error')
        return 'Unknown'
      }
    })
}

/** start a probe that gives up after the timeout; returns its abort */
export const startGeoProbe = (
  onResult: (result: GeoResult | 'Unknown') => void
) => {
  const controller = new AbortController()
  const timeoutId = setTimeout(
    () => controller.abort(),
    GEO_MANIFEST_FETCH_TIMEOUT_MS
  )
  // stopped by the caller (an unmount): no result after that
  let stopped = false
  probeGeoManifest(controller.signal)
    .then(result => {
      if (!stopped) {
        onResult(result)
      }
    })
    .finally(() => clearTimeout(timeoutId))
  return () => {
    stopped = true
    clearTimeout(timeoutId)
    controller.abort()
  }
}

export const useGeoManifestFetchStatus = (): GeoStatus => {
  const [country, setCountry] = useState<GeoResult>('Israel')
  const [settled, setSettled] = useState(false)
  useEffect(
    () =>
      startGeoProbe(result => {
        // no answer at all: fail open to the global stream, as before
        setCountry(result === 'Israel' ? 'Israel' : 'Not Israel')
        setSettled(true)
      }),
    []
  )

  return { country, settled }
}

// Backwards-compatible default export: just the country (defaults to 'Israel').
// The portal player recomputes its live entry on every render and remounts the
// Kaltura player when the entry changes, so it self-corrects once geo settles
// and does not need to wait on `settled`.
const useGeoManifestFetch = (): GeoResult => useGeoManifestFetchStatus().country
export default useGeoManifestFetch
